import {
  GamesGenderSportIndexQuery,
  GamesGenderSportIndexQueryVariables,
  LevelEnum,
  OrganizationTypeEnum,
  RankingFormulaEnum,
  rubyQueries
} from 'queries';
import { Fragment, useEffect, useMemo, useState } from 'react';
import Datepicker from '@/components/Datepicker';
import compact from 'helpers/compact';
import CalendarModal from '@/components/CalendarModal';
import styles from './styles/GenderSportIndex.module.scss';
import pluralize from 'helpers/pluralize';
import dayjs from 'dayjs';
import { useApplicationContainer } from 'containers/ApplicationContainer';
import merge from 'helpers/merge';
import useFavoriteGames from 'hooks/useFavoriteGames';
import isToday from 'dayjs/plugin/isToday';
import PageLayout from '@/components/PageLayout';
import Page from '@/components/Page';
import ContentSection from '@/components/ContentSection';
import cn from 'classnames';
import PageHeader from '@/components/PageHeader';
import SectionHeader, { SectionHeaderSize, SectionHeaderTag } from '@/components/SectionHeader';
import TextLink, { TextLinkSize } from '@/components/TextLink';
import groupBy from 'helpers/groupBy';
import Filter from '@/components/Filter';
import PageFilters from '@/components/PageFilters';
import LocalizedDate from 'components/LocalizedDate';
import InfinitePagination from '@/components/InfinitePagination';
import { entryPoint } from 'react_rails/entryPoint';
import { useActiveGames } from 'stores/activeGamesStore';
import { WebScoreboardInfiniteBlocks } from '@/components/web/WebScoreboard';
import WebScoreboardGame from '@/components/web/WebScoreboardGame';
import HeadlinesModule from '@/components/modules/HeadlinesModule';
import RankingsModule from '@/components/modules/RankingsModule';
import filterArticles from 'helpers/filterArticles';
import { SpinnerLocation } from '@/components/Spinner';
import MinuteMediaAd from '@/components/ads/MinuteMediaAd';
import STATES from 'constants/States';
import { trackFathomEvent } from 'helpers/trackFathomEvent';

dayjs.extend(isToday);

type OrganizationType = NonNullable<GamesGenderSportIndexQuery['stateGenderSportOrganizations']>[number];
type OrganizationOptions = (OrganizationType | [string, OrganizationType[]])[];

function dateToString<T extends Date | undefined, R extends T extends Date ? string : undefined>(date: T): R {
  if (date === undefined) {
    return undefined as R;
  }
  return dayjs(date).format('YYYY-MM-DD') as R;
}

export default entryPoint(function GenderSportIndex({
  query,
  variables
}: {
  query: GamesGenderSportIndexQuery & {
    scoreboardDate: {
      date: NonNullable<GamesGenderSportIndexQuery['scoreboardDate']['date']>;
    };
    stateGenderSportOrganizations: OrganizationType[];
  };
  variables: GamesGenderSportIndexQueryVariables;
}) {
  const [{ genderSport, state, pageTitle, pathname }, { handleShare, handleShareGame, handleStartLoading, handleStopLoading, makePath }] =
    useApplicationContainer();
  const [calendarDates, setCalendarDates] = useState<Record<string, number | undefined>>({});
  const [calendarIsOpen, setCalendarIsOpen] = useState(false);
  const [games, setGames] = useState(query.scoreboardDate.games.nodes);

  const organization = useMemo(
    () => query.stateGenderSportOrganizations.find(organization => organization.id === variables.organizationId),
    [variables.organizationId]
  );
  const [selectedOrganizationType, setSelectedOrganizationType] = useState(organization?.label);

  const date = dayjs(query.scoreboardDate.date).toDate();
  const activeGames = useActiveGames(dayjs(date).isToday());
  const favoriteGames = useFavoriteGames({ ...variables, date: dateToString(date) });

  const dateOptions = [
    //query.scoreboardDate.previousScoreboardDate?.previousScoreboardDate?.previousScoreboardDate?.date,
    query.scoreboardDate.previousScoreboardDate?.previousScoreboardDate?.date,
    query.scoreboardDate.previousScoreboardDate?.date,
    query.scoreboardDate.date,
    query.scoreboardDate.nextScoreboardDate?.date,
    query.scoreboardDate.nextScoreboardDate?.nextScoreboardDate?.date
    //query.scoreboardDate.nextScoreboardDate?.nextScoreboardDate?.nextScoreboardDate?.date
  ]
    .filter(compact)
    .map(date => dayjs(date).toDate());

  const organizationOptions = useMemo(() => {
    return Object.entries(groupBy(query.stateGenderSportOrganizations, org => org.label)).reduce(
      (options: Record<string, OrganizationOptions>, [label, organizations]) => {
        const organizationsByParentId = groupBy(organizations, organization => organization.parentId || '-');

        if (Object.keys(organizationsByParentId).length <= 1) {
          return { ...options, [label]: organizations };
        }

        const nestedOrganizations = Object.entries(organizationsByParentId).reduce(
          (organizationOptions: OrganizationOptions, [parentId, childOrganizations]) => {
            const parent = query.stateGenderSportOrganizations.find(organization => organization.id === parentId);

            if (!parent) {
              return organizationOptions.concat(childOrganizations);
            }

            organizationOptions.push([parent.fullName || parent.name, childOrganizations]);
            return organizationOptions;
          },
          []
        );

        return { ...options, [label]: nestedOrganizations };
      },
      {}
    );
  }, [query.stateGenderSportOrganizations]);

  const subHeader = useMemo(() => {
    const top25 = variables.power25 ? 'Top 25' : undefined;

    if (!state) return [top25, 'National High School'].filter(compact).join(' ');

    if (organization) {
      return [organization.name, top25].filter(compact).join(' ');
    }

    const associations = query.stateGenderSportOrganizations.filter(org => org.organizationTypeKey === OrganizationTypeEnum.ASSOCIATION);
    if (associations.length === 1) {
      return [associations[0].name, top25].filter(compact).join(' ');
    }

    return null;
  }, [state, query.stateGenderSportOrganizations]);

  const blurb = useMemo(() => {
    if (!query.scoreboardDate.blurb) return null;

    return query.scoreboardDate.blurb.split(/(\[[^)]+\)|\d{10})/).map((part, i) => {
      const [_, text, path] = part.match(/\[([^\]]+)\]\(([^\)]+)\)/) || [];
      if (text && path) {
        return <TextLink key={i} href={path} size={TextLinkSize.INHERIT} dangerouslySetInnerHTML={{ __html: text }} />;
      } else if (/\d{10}/.test(part)) {
        return <LocalizedDate key={i} date={dayjs(+part * 1000).toString()} format="h:mm A" />;
      } else {
        return <Fragment key={i}>{part}</Fragment>;
      }
    });
  }, [query.scoreboardDate.blurb]);

  const relatedOrganizations = useMemo(() => {
    if (organization) {
      const children = query.stateGenderSportOrganizations.filter(org => org.parentId === organization.id);
      if (children.length > 0) return children;

      return query.stateGenderSportOrganizations.filter(org => org.parentId === organization.parentId);
    } else {
      const sections = query.stateGenderSportOrganizations.filter(org => org.organizationTypeKey === OrganizationTypeEnum.SECTION);
      if (sections.length > 0) return sections;

      return query.stateGenderSportOrganizations.filter(org => org.organizationTypeKey === OrganizationTypeEnum.DIVISION);
    }
  }, [query.stateGenderSportOrganizations]);

  useEffect(() => {
    setGames(games => games.map(game => merge(game, activeGames[game.id] || {})));
  }, [activeGames]);

  useEffect(() => {
    setGames(games => [...favoriteGames, ...games.filter(game => !favoriteGames.some(favoriteGame => favoriteGame.id === game.id))]);
  }, [favoriteGames]);

  function handleChangeMonth(date: Date) {
    // TODO put this in calendar so it always returns first day of month (need it to be consistent for caching)
    const firstDayOfMonth = dayjs(date).startOf('month').toDate();

    if (Object.keys(calendarDates).some(date => dayjs(date).isSame(firstDayOfMonth, 'month'))) return;

    handleStartLoading(SpinnerLocation.CENTER);

    rubyQueries
      .gamesGenderSportIndexCalendarQuery(300, {
        ...variables,
        date: dateToString(firstDayOfMonth)
      })
      .then(({ scoreboardDates }) =>
        setCalendarDates(currentCalendarDates => {
          const calendarDates = { ...currentCalendarDates };
          scoreboardDates.forEach(scoreboardDate => {
            calendarDates[scoreboardDate.date] = scoreboardDate.games.totalCount;
          });
          return calendarDates;
        })
      )
      .finally(handleStopLoading);
  }

  function handleCloseCalendar() {
    setCalendarIsOpen(false);
  }

  function handleOpenCalendar() {
    handleChangeMonth(date);
    setCalendarIsOpen(true);
  }

  function makeCalendarDatePath(date: Date) {
    const gamesCount = calendarDates[dateToString(date)];
    if (!gamesCount || gamesCount === 0) return;

    return makeScoresPath({ date });
  }

  function makeScoresPath({
    date: dateParam,
    organization: organizationParam,
    level: levelParam,
    state: stateParam,
    top25
  }: {
    date?: Date;
    organization?: { slug: string } | null;
    level?: { key: LevelEnum };
    state?: { slug: string };
    top25?: boolean;
  }) {
    const level = levelParam?.key || variables.level;

    return makePath(
      {
        genderSport,
        state: stateParam || state,
        organization: organizationParam === null ? undefined : organizationParam || organization,
        path: ['scores', (top25 !== undefined ? top25 : variables.power25) ? 'top-25' : undefined].filter(compact).join('/')
      },
      { date: dateToString(dateParam || date), level: level === LevelEnum.VARSITY ? undefined : level }
    );
  }

  return (
    <>
      {calendarIsOpen && (
        <CalendarModal
          value={dayjs(query.scoreboardDate.date).toDate()}
          onClose={handleCloseCalendar}
          makeDatePath={makeCalendarDatePath}
          onChangeMonth={handleChangeMonth}
        />
      )}
      <Page>
        <PageLayout
          sidebar={
            <>
              <MinuteMediaAd placement="SIDEBAR" />
              {query.articles.nodes.length > 0 && <HeadlinesModule articles={filterArticles(query.articles.nodes)} href={makePath()} />}
              <MinuteMediaAd placement="SIDEBAR_2" />
              {query.ranking && (
                <RankingsModule
                  href={makePath({ path: 'rankings' })}
                  teamRankings={query.ranking.teamRankings.nodes}
                  title={[state?.name, query.ranking.formula === RankingFormulaEnum.POWER_25 && 'Power 25', 'Rankings']
                    .filter(compact)
                    .join(' ')}
                />
              )}
            </>
          }
        >
          <ContentSection className={styles.GenderSport}>
            <PageHeader>{pageTitle}</PageHeader>
            <PageFilters>
              {query.scoreboardDate.hasPower25 && (
                <Filter
                  options={['All Games', 'Top 25']}
                  pathExtractor={option => makeScoresPath({ top25: option === 'Top 25', level: { key: LevelEnum.VARSITY } })}
                  value={variables.power25 ? 'Top 25' : 'All Games'}
                />
              )}
              <Filter
                placeholder="All States"
                options={STATES.filter(state => state.isAmerican)}
                labelExtractor={state => state.name}
                value={state}
                pathExtractor={state => makePath({ state: state || null, path: pathname.split('/').at(-1) })}
              />
              {variables.level === LevelEnum.VARSITY && Object.keys(organizationOptions).length > 1 && (
                <>
                  <Filter
                    placeholder="Sort By"
                    options={Object.keys(organizationOptions)}
                    value={selectedOrganizationType}
                    onChange={setSelectedOrganizationType}
                  />
                  {Object.keys(organizationOptions).map(groupType => (
                    <Filter
                      key={groupType}
                      placeholder={`All ${pluralize(groupType)}`}
                      options={organizationOptions[groupType]}
                      labelExtractor={org => org.name}
                      pathExtractor={org => makeScoresPath({ organization: org || { slug: '' } })}
                      value={organization}
                      className={cn({ [styles.HiddenFilter]: groupType !== selectedOrganizationType })}
                    />
                  ))}
                </>
              )}
            </PageFilters>
            <Datepicker
              options={dateOptions}
              makeDatePath={date => makeScoresPath({ date })}
              onClickCalendar={handleOpenCalendar}
              value={date}
            />
            {subHeader && (
              <div className={styles.Subheader}>
                <SectionHeader tag={SectionHeaderTag.H2} size={SectionHeaderSize.SMALL} flush>
                  {subHeader} Scoreboard
                </SectionHeader>
                <div className={styles.GamesCount}>
                  {query.scoreboardDate.games.totalCount} {pluralize('game', query.scoreboardDate.games.totalCount)}
                </div>
              </div>
            )}
            <InfinitePagination
              className={styles.Scoreboard}
              onLoadMore={(
                {
                  scoreboardDate: {
                    games: { nodes, pageInfo }
                  }
                },
                currentPage
              ) => {
                setGames(games => [...games, ...nodes.filter(game => !favoriteGames.some(favoriteGame => favoriteGame.id === game.id))]);
                trackFathomEvent(`scroll_to_page_${currentPage}`);
                return pageInfo;
              }}
              pageInfo={query.scoreboardDate.games.pageInfo}
              query={rubyQueries.gamesGenderSportIndexQuery}
              ttl={dayjs(date).isToday() ? 30 : 30 * 5}
              variables={{ ...variables, paginating: true }}
            >
              <WebScoreboardInfiniteBlocks games={games}>
                {game => <WebScoreboardGame key={game.id} game={game} showState={!state} showRecords={!!state} />}
              </WebScoreboardInfiniteBlocks>
            </InfinitePagination>
            {blurb && (
              <div className={styles.Blurb}>
                <div className={styles.BlurbText}>{blurb}</div>
                <SectionHeader size={SectionHeaderSize.EXTRA_SMALL}>Related Content</SectionHeader>
                <div className={styles.BlurbLinks}>
                  <TextLink href={makePath()}>
                    {state?.name || 'High School'} {genderSport?.name} News
                  </TextLink>
                  <TextLink href={makePath({ path: 'rankings' })}>
                    {state?.name || 'High School'} {genderSport?.name} Rankings
                  </TextLink>
                  <TextLink href={makePath({ path: 'teams' })}>
                    {state?.name || 'High School'} {genderSport?.name} Teams
                  </TextLink>
                </div>
              </div>
            )}
          </ContentSection>
        </PageLayout>
      </Page>
    </>
  );
});
